// @givmo/api-client — money convention.
//
// HARD RULE: money crosses the wire as a DECIMAL STRING and is never parsed into
// a JavaScript `number` for arithmetic or storage. The backend OpenAPI snapshot
// models monetary fields as `anyOf: [{type: number}, {type: string,
// pattern: ...}]` on the request side and as plain `string` on the response side
// (e.g. `CompanyMatchDonationOut.amount`). IEEE-754 doubles cannot represent
// most decimal cent values exactly (0.1 + 0.2 !== 0.3), so any `parseFloat` on a
// money field is a correctness bug, not a style preference.
//
// This module is the single sanctioned representation. UI formatting and any
// arithmetic happen on the decimal string via a decimal library at the
// call-site; this package only guarantees the wire value is carried verbatim.

declare const moneyBrand: unique symbol;

/**
 * A monetary amount as an exact decimal string (e.g. `"10.00"`, `"1234.56"`).
 * Branded so a raw `string` or `number` cannot be passed where a `MoneyString`
 * is required without going through {@link toMoneyString}.
 */
export type MoneyString = string & { readonly [moneyBrand]: 'MoneyString' };

/** Matches the backend's money pattern: optional sign, digits, optional fraction. */
const MONEY_PATTERN = /^[+-]?\d*\.?\d+$/;

/**
 * Validate + brand a wire/user value as a {@link MoneyString}. Accepts a string
 * (validated against the decimal pattern) or — only as an ingress convenience —
 * a finite `number`, which is converted with `String(...)` and then validated.
 *
 * Throws on a non-finite number or a non-decimal string. It deliberately does
 * NOT round or reformat: the caller owns presentation and precision.
 */
export function toMoneyString(value: string | number): MoneyString {
  const raw = typeof value === 'number' ? numberToDecimalString(value) : value;
  if (!MONEY_PATTERN.test(raw)) {
    // Never echo the raw value into the message — a money string is not secret
    // but keeping error text value-free matches the PII-safe logging posture.
    throw new RangeError('Invalid money value: not a decimal string');
  }
  return raw as MoneyString;
}

/**
 * Read a money field off a decoded response body. Backend response money fields
 * are typed as plain strings; this narrows + brands them without parsing.
 * Returns `null` for absent/null fields so optional amounts stay optional.
 */
export function readMoneyString(value: unknown): MoneyString | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && MONEY_PATTERN.test(value)) {
    return value as MoneyString;
  }
  // A number on the wire is tolerated (request-side anyOf allows it) but is a
  // smell on the response side — brand its string form rather than its double.
  if (typeof value === 'number' && Number.isFinite(value)) {
    return numberToDecimalString(value) as MoneyString;
  }
  return null;
}

function numberToDecimalString(value: number): string {
  if (!Number.isFinite(value)) {
    throw new RangeError('Invalid money value: not a finite number');
  }
  // String(1.5) === '1.5'; avoids the scientific-notation that .toString() can
  // emit for very large/small magnitudes (not expected for donation amounts,
  // but defensive).
  return Number.isInteger(value) ? value.toString() : value.toString();
}

/** How {@link formatMoneyDisplay} shows the cents. */
export interface MoneyDisplayOptions {
  /**
   * `'always'` (the default) shows exactly two decimals (`"60000"` → `"60,000.00"`). `'when-present'`
   * shows them only when the amount, rounded to the cent, has some: `"60000.00"` → `"60,000"`, while
   * `"60000.5"` → `"60,000.50"` keeps both digits.
   */
  cents?: 'always' | 'when-present';
}

/**
 * Normalize a decimal money STRING to a consistent display form:
 * `<thousands-grouped integer>.<exactly 2 decimals>` (e.g. `"5240.5"` → `"5,240.50"`,
 * `"8420.75"` → `"8,420.75"`, `"1000"` → `"1,000.00"`), or, with `cents: 'when-present'`, the
 * grouped integer alone when the cents round to zero (`"1000"` → `"1,000"`).
 *
 * MONEY LAW (HARD): the value is NEVER parsed to a binary float. Normalization is done entirely on
 * the decimal string — split on the dot, round the fractional part to 2 places via integer digit
 * math (half-up on the 3rd fractional digit), and group the integer part with thousands separators.
 * No IEEE-754 round-trip. Returns `null` if `value` isn't a plain `[-]?digits[.digits]` decimal, so
 * callers can fall back to rendering the raw string verbatim (tolerant-reader law).
 */
export function formatMoneyDisplay(
  value: string,
  options: MoneyDisplayOptions = {},
): string | null {
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return null;
  const sign = match[1] === '-' ? '-' : '';
  let intPart = match[2] ?? '0'; // the `\d+` group always matches; `?? '0'` only satisfies the type
  const fracRaw = match[3] ?? '';

  // Round to 2 decimals via integer digit math on the string (half-up).
  let frac = fracRaw.slice(0, 2).padEnd(2, '0');
  const nextDigit = fracRaw.charCodeAt(2) - 48; // 48 = '0'; negative/NaN when absent
  if (nextDigit >= 5) {
    const carried = addOneToDigits(intPart + frac); // operate on the digit string, no float
    intPart = carried.slice(0, carried.length - 2);
    frac = carried.slice(carried.length - 2);
  }
  intPart = intPart.replace(/^0+(?=\d)/, ''); // trim leading zeros, keep one digit

  let grouped = '';
  for (let i = 0; i < intPart.length; i++) {
    if (i > 0 && (intPart.length - i) % 3 === 0) grouped += ',';
    grouped += intPart[i]!;
  }
  // Decided on the ROUNDED cents, so "9.999" (→ "10.00") reads "10", never "10.00" or "9.99".
  if (options.cents === 'when-present' && frac === '00') return `${sign}${grouped}`;
  return `${sign}${grouped}.${frac}`;
}

/** Add 1 to a non-negative integer represented as a digit string, without numeric conversion. */
function addOneToDigits(digits: string): string {
  const out = digits.split('');
  let i = out.length - 1;
  let carry = 1;
  while (i >= 0 && carry > 0) {
    const sum = out[i]!.charCodeAt(0) - 48 + carry;
    out[i] = String.fromCharCode((sum % 10) + 48);
    carry = sum >= 10 ? 1 : 0;
    i--;
  }
  return carry > 0 ? `1${out.join('')}` : out.join('');
}

/**
 * Convert INTEGER CENTS to an exact decimal-string dollar amount via BigInt digit math — NO float
 * (`cents / 100` is exactly the IEEE-754 trap the money law bans: `1499 / 100 → 14.99` is fine, but
 * the operation is a double round-trip and larger magnitudes drift). Only a genuine integer (or an
 * all-digits string) converts; anything else → `null` (tolerant reader — never guess about money).
 *
 * The single audited home for this conversion. Both the super-admin pledge-campaign adjudication
 * decode and the donate guest-pledge decode import it from here (never a local `cents / 100`), so
 * the exact-cents formatting for `rate_cents` / `cap_cents` / `default_cap_cents` and the
 * cap-restatement math is one implementation, tested once. Distinct from {@link readMoneyString}
 * (which brands an already-DOLLARS wire value verbatim) and {@link formatMoneyDisplay} (2dp +
 * thousands grouping of a dollar string) — this one owns the cents→dollars step that precedes them.
 */
export function centsToMoneyString(value: unknown): string | null {
  let cents: bigint;
  if (typeof value === 'number' && Number.isInteger(value)) {
    cents = BigInt(value);
  } else if (typeof value === 'string' && /^-?\d+$/.test(value)) {
    cents = BigInt(value);
  } else {
    return null;
  }
  const negative = cents < 0n;
  const abs = (negative ? -cents : cents).toString().padStart(3, '0');
  const dollars = abs.slice(0, abs.length - 2);
  const frac = abs.slice(abs.length - 2);
  return `${negative ? '-' : ''}${dollars}.${frac}`;
}
